import { Separator } from "@/components/ui/separator";
import { Dot, EllipsisVertical, User, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import { createBookingTable, createRoom, createTable, updateBookingTable, updateRoom, updateTable, getBookingById } from "@/api/table-floor/tableFloor";
import { useSelector, useDispatch } from "react-redux";
import { closeAddNewBookingTableModal, fetchAllTables } from "@/redux/slice/table-slice/table-slice";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import CustomerMultiSelect from "@/components/loyalty/CustomerMultiSelect";
import { getAllCustomerApi } from "@/api/customer/customer";
import { getAllStaff } from "@/api/staff";
import { formatTime } from "@/lib/bookingUtils";
import { fetchAllStaff } from "@/redux/slice/staff-management/staff-attendance";
import { format } from "date-fns";

const addNewFloorCardSchema = z.object({
  customer_phone: z.string().min(5, "Phone number is required"),
  customer_name: z.string().min(1, "Name is required"),
  customer_email: z.string().min(1, "Email is required"),
  reservation_type: z.string().min(1, "Reservation type is required"),
  staff_id: z.coerce.number().optional(),
  reservation_date: z.preprocess((val) => {
    if (!val) return undefined;
    if (typeof val === "string") return val.split("T")[0];
    if (val instanceof Date) return format(val, "yyyy-MM-dd");
    return String(val);
  }, z.string({ required_error: "End date is required" })),
  start_time: z.string().min(1, "Start time is required"),
  end_time: z.string().min(1, "End time is required"),
  guest: z.coerce.number().min(1, "Guest is required"),
  advance: z.coerce.number().optional(),
  note: z.string().optional(),
  status: z.string().optional(),
});

const AddNewBookingForm = ({ isOpen, onClose, formType, setFormType, bookingItemForEditId, floorName }) => {
  // console.log(bookingItemForEdit?.id);
  const { tables } = useSelector((state) => state.table.table);
  const [bookingItemForEdit, setBookingItemForEdit] = useState(null)

  // console.log(formType, bookingItemForEdit, floorName);

  const { floors, rooms, selectedRoom } = useSelector((state) => state.table.table);
  const { staffList } = useSelector(
    (state) => state.staffManage.attendance
  );
  const [loading, setLoading] = useState(false);
  const [showIsChild, setShowIsChild] = useState(false);
  const { user } = useAuth();
  const [staff, setStaff] = useState([])
  const [selectedPayerUser, setSelectedPayerUser] = useState([]);
  const dispatch = useDispatch();
  // console.log(staff);
  useEffect(() => {
    if (!isOpen) return;

    if (bookingItemForEditId) {
      const fetchBooking = async () => {
        try {
          const res = await getBookingById(bookingItemForEditId);
          if (res.status === "success" && res.data?.booking) {
            console.log(res.data.booking, "text booking details from api");
            setBookingItemForEdit(res.data.booking);
          } else {
            console.error("Failed to fetch booking details:", res?.message);
            const localBooking = tables
              .flatMap((table) => table.bookings || [])
              .find((b) => b.id === bookingItemForEditId);
            setBookingItemForEdit(localBooking);
          }
        } catch (error) {
          console.error("Error fetching booking details:", error);
          const localBooking = tables
            .flatMap((table) => table.bookings || [])
            .find((b) => b.id === bookingItemForEditId);
          setBookingItemForEdit(localBooking);
        }
      };
      fetchBooking();
    } else {
      setBookingItemForEdit(null);
    }
  }, [isOpen, bookingItemForEditId]);

  const form = useForm({
    resolver: zodResolver(addNewFloorCardSchema),
    mode: "onChange",
    defaultValues: {
      customer_phone: "",
      customer_name: "",
      customer_email: "",
      reservation_type: "",
      staff_id: "",
      // issue_date: "",
      reservation_date: format(new Date(), "yyyy-MM-dd"),
      start_time: "",
      end_time: "",
      guest: "",
      advance: "",
      note: "",
      status: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      setBookingItemForEdit(null);
      form.reset({
        customer_phone: "",
        customer_name: "",
        customer_email: "",
        reservation_type: "",
        staff_id: "",
        reservation_date: format(new Date(), "yyyy-MM-dd"),
        start_time: "",
        end_time: "",
        guest: "",
        advance: "",
        note: "",
        status: "",
      });
    } else {
      setBookingItemForEdit(null);
    }
  }, [isOpen, bookingItemForEditId]);

  // useEffect(() => {
  //   if (customer && customer.length > 0) return;
  //   getAllStaff()
  //     .then((res) => {
  //       if (res.status === "success") {
  //         setCustomer(res.data.allStaff);
  //       }
  //     })
  //     .catch((err) => console.log(err));
  // }, [])
  // console.log(customer);
  // console.log(form.formState.errors);
  useEffect(() => {
    if (showIsChild && staffList.length === 0) {
      dispatch(fetchAllStaff());
    }
  }, [showIsChild])

  useEffect(() => {
    if (formType === "update" && bookingItemForEdit) {
      setShowIsChild(false)
      form.reset({
        customer_phone: bookingItemForEdit?.customer?.number || "",
        customer_name: bookingItemForEdit?.customer?.full_name || "",
        customer_email: bookingItemForEdit?.customer?.email || "",
        reservation_type: bookingItemForEdit?.reservation_type || "",
        staff_id: bookingItemForEdit?.staff_id || "",
        reservation_date: bookingItemForEdit?.reservation_date ? bookingItemForEdit.reservation_date.split("T")[0] : "",
        start_time: bookingItemForEdit?.start_time || "",
        end_time: bookingItemForEdit?.end_time || "",
        guest: bookingItemForEdit?.guest || "",
        advance: bookingItemForEdit?.advance || "",
        note: bookingItemForEdit?.note || "",
        status: bookingItemForEdit?.status || "",
      });
      if (bookingItemForEdit?.staff_id) {
        setShowIsChild(true);
      }
    }
    if (formType === "create") {
      form.reset(form.defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, bookingItemForEdit]);

  const { reset } = form;

  function removeSeconds(time) {
    const parts = time.split(":");

    // যদি HH:mm:ss হয় (৩টা অংশ)
    if (parts.length === 3) {
      return parts[0] + ":" + parts[1];
    }

    // যদি already HH:mm হয়
    return time;
  }

  const addFloorCardData = async (data) => {
    // console.log(form.error())
    // console.log(data);
    // return;

    setLoading(true);

    const payload =
      formType === "update"
        ? {
          ...data,
          start_time: removeSeconds(data.start_time),
          end_time: removeSeconds(data.end_time),
          restaurant_id: user?.restaurant_id,
          staff_id: showIsChild ? data.staff_id : null,
          // table_size: {
          //   ...bookingItemForEdit.table_size,
          //   name: data.name,
          // },
        }
        : {
          ...data,
          restaurant_id: user?.restaurant_id,
          table_id: bookingItemForEdit.id,
          staff_id: showIsChild ? data.staff_id : null,
        };
    // console.log(payload);
    // return

    try {
      let res;

      if (formType === "update") {
        res = await updateBookingTable(bookingItemForEdit.id, payload);
      } else {
        res = await createBookingTable(payload);
      }

      if (res.status === "success") {
        if (formType === "update") {
          toast.success("Booked item updated successfully!");
        } else {
          toast.success("Booked table successfully!");
        }
        onClose()
        reset();
        // IMPORTANT: refresh tables for selected room to reflect changes immediately
        if (data?.room_id || selectedRoom?.id) {
          const roomId = data.room_id || selectedRoom?.id;
          dispatch(fetchAllTables(Number(roomId)));
        }
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Floor Card");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
        <Card className="bg-cblack4 text-cwhite w-[600px] h-[95vh] overflow-scroll">
          <CardHeader className="flex flex-row justify-between items-center">
            <CardTitle className="text-cwhite text-sm font-medium">{formType === "update" ? "Update 1" : formType === "create" ? 'New' : 'View'}Table Booking: {floorName}</CardTitle>
            <X onClick={onClose} className="cursor-pointer" size={16} />
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-8">
              <Form {...form}>
                {formType === 'view' ?
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Phone Number</label>
                      <input
                        className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={bookingItemForEdit?.customer?.number}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Customer Name</label>
                      <input
                        className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={bookingItemForEdit?.customer?.full_name}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Email Address</label>
                      <input
                        className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={bookingItemForEdit?.customer?.email}
                        readOnly
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Reservation Type</label>
                        <input
                          className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                          value={bookingItemForEdit?.reservation_type}
                          readOnly
                        />
                      </div>
                      {bookingItemForEdit?.staff_id &&
                        <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Assign Staff</label>
                          <input
                            className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                            value={bookingItemForEdit?.staff_name}
                            readOnly
                          />
                        </div>
                      }
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="col-span-2">
                        <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Date</label>
                          <input
                            className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                            value={bookingItemForEdit?.reservation_date?.split('T')[0]}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Start Time</label>
                          <input
                            className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                            value={formatTime(bookingItemForEdit?.start_time)}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">End Time</label>
                          <input
                            className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                            value={formatTime(bookingItemForEdit?.end_time)}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div>
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Table Number</label>
                        <input
                          className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                          value={floorName}
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Number Of Guest</label>
                        <input
                          className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                          value={bookingItemForEdit?.guest}
                          readOnly
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Advance Amount</label>
                      <input
                        className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={bookingItemForEdit?.advance}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Notes</label>
                      <input
                        className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={bookingItemForEdit?.note}
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Status</label>
                      <input
                        className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={bookingItemForEdit?.status}
                        readOnly
                      />
                    </div>
                    <div className="mt-4 flex gap-5 w-full">
                      <Button isLoading={loading} className="w-full bg-cyellow text-base font-medium" onClick={() => { setFormType('update') }}>
                        Edit
                      </Button>
                    </div>
                  </div> :
                  <form onSubmit={form.handleSubmit(addFloorCardData)} className="flex flex-col gap-5">
                    <FormInputField name="customer_phone" style="bg-cblack3" placeholder={"Write Phone Number"} form={form} type="text" label={"Phone Number"} required={true} />
                    <FormInputField name="customer_name" style="bg-cblack3" placeholder={"Write Customer Name"} form={form} type="text" label={"Customer Name"} required={true} />
                    <FormInputField name="customer_email" style="bg-cblack3" placeholder={"Write Email Address"} form={form} type="text" label={"Email Address"} required={true} />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <FormInputField
                          name="reservation_type"
                          style="bg-cblack3"
                          placeholder={"Reservation Type*"}
                          form={form}
                          type="select"
                          parseAs="string"
                          selectOption={[
                            { label: "In Person", value: "in_person" },
                            { label: "Phone Call", value: "phone" },
                            { label: "Online", value: "online" },
                            { label: "Other", value: "other" },
                          ]}
                          label={"Reservation Type"}
                          required={true}
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex gap-2 items-center">
                          <Switch
                            className="data-[state=unchecked]:bg-neutral-500"
                            checked={showIsChild}
                            onCheckedChange={(val) => setShowIsChild(val)}
                          />
                          <Label>Assign Staff</Label>
                        </div>

                        {showIsChild && (
                          <FormInputField
                            name="staff_id"
                            style="bg-cblack3"
                            placeholder={"Select Staff"}
                            form={form}
                            type="select"
                            parseAs="string"
                            selectOption={staffList?.map(item => ({
                              label: item?.user?.first_name + " " + item?.user?.last_name,
                              value: item?.user?.id,
                            }))}
                          />
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="col-span-2">
                        <FormInputField name="reservation_date" style="bg-cblack3" placeholder={"Select Date"} form={form} type="date" dateFormat="yyyy-MM-dd" label={"Date"} required={true} />
                      </div>
                      <div className="col-span-1">
                        <FormInputField name="start_time" style="bg-cblack3" placeholder={"Select Start Time"} form={form} type="time" label={"Start Time"} required={true} />
                      </div>
                      <div className="col-span-1">
                        <FormInputField name="end_time" style="bg-cblack3" placeholder={"Select End Time"} form={form} type="time" label={"End Time"} />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div>
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Table Number</label>
                        <input
                          className="flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                          value={floorName}
                          readOnly
                        />
                      </div>
                      <FormInputField name="guest" style="bg-cblack3" placeholder={"Enter Number of Guest"} form={form} type="number" label={"Number Of Guest"} required={true} />
                    </div>
                    <FormInputField name="advance" style="bg-cblack3" placeholder={"Enter Advance Amount"} form={form} type="number" label={"Advance Amount"} />
                    <FormInputField name="note" style="bg-cblack3" placeholder={"Enter Notes"} form={form} type="textarea" label={"Notes"} />
                    <FormInputField
                      name="status"
                      style="bg-cblack3"
                      placeholder={"Select Status"}
                      form={form}
                      type="select"
                      parseAs="string"
                      selectOption={
                        formType === "update"
                          ? [
                            { label: "Pending", value: "pending" },
                            { label: "Confirmed", value: "confirmed" },
                            { label: "Cancelled by Customer (CC)", value: "Cancelled by Customer (CC)" },
                            { label: "Cancelled by Admin (CA)", value: "Cancelled by Admin (CA)" },
                          ]
                          : [
                            { label: "Pending", value: "pending" },
                            { label: "Confirmed", value: "confirmed" },
                          ]
                      }
                      label={"Status"}
                    />
                    <div className="mt-4 flex gap-5 w-full">
                      <Button className="w-full bg-cred text-white" onClick={onClose}>
                        Cancel
                      </Button>

                      <Button isLoading={loading} className="w-full bg-cyellow">
                        {formType === "update" ? " Update" : " Confirm"}
                      </Button>
                    </div>
                  </form>
                }
              </Form>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AddNewBookingForm;
